import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
} from '@mui/material';
import { Settings as SettingsIcon } from '@mui/icons-material';
import api from '../services/api';

import {
  JiraRow,
  SlackRow,
  GitHubRow,
  GoogleDriveRow,
  AiToolsRow,
  SimilarityRow,
  EmbeddingsRow,
  SmartSearchRow,
  HelpRow,
  StorageRow,
} from '../components/settings';

/* ================================================================== */
/*  Section header — lightweight label above each accordion group      */
/* ================================================================== */
const SectionHeader = ({ label }) => (
  <Box sx={{ mt: 3.5, mb: 1 }}>
    <Typography
      variant="overline"
      sx={{ color: 'text.secondary', letterSpacing: 1.5, fontWeight: 700, fontSize: '0.7rem' }}
    >
      {label}
    </Typography>
    <Divider sx={{ mt: 0.5 }} />
  </Box>
);

/* ================================================================== */
/*  Main component                                                     */
/* ================================================================== */
const ApplicationSettings = () => {
  /* ---- Loading / notification state ---- */
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [populatingEmbeddings, setPopulatingEmbeddings] = useState(false);
  const [embeddingProgress, setEmbeddingProgress] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [canEdit, setCanEdit] = useState(false);
  const pollIntervalRef = useRef(null);

  /* ---- Settings state ---- */
  const [threshold, setThreshold] = useState(75);
  const [selectedModel, setSelectedModel] = useState('');
  const [supportedModels, setSupportedModels] = useState({});
  const [modelsStatus, setModelsStatus] = useState({});
  const [embeddingStats, setEmbeddingStats] = useState(null);

  /* ---- Smart Search / LLM ---- */
  const [smartSearchSettings, setSmartSearchSettings] = useState(null);
  const [loadingSmartSearch, setLoadingSmartSearch] = useState(false);
  const [savingSmartSearch, setSavingSmartSearch] = useState(false);
  const [minSimilarity, setMinSimilarity] = useState(50);
  const [minConfidence, setMinConfidence] = useState(50);
  const [cacheTTL, setCacheTTL] = useState(60);
  const [maxResults, setMaxResults] = useState(50);

  /* ---- Slack ---- */
  const [slackStatus, setSlackStatus] = useState({ connected: false, configured: false });
  const [loadingSlack, setLoadingSlack] = useState(false);

  /* ---- JIRA ---- */
  const [jiraStatus, setJiraStatus] = useState({ connected: false, configured: false });
  const [loadingJira, setLoadingJira] = useState(false);

  /* ---- QAssure AI Tools ---- */
  const [copilotSettings, setCopilotSettings] = useState(null);
  const [loadingCopilot, setLoadingCopilot] = useState(false);
  const [savingCopilot, setSavingCopilot] = useState(false);
  const [toolToggles, setToolToggles] = useState({
    portal: true, github: true, jira: false, mysql: false,
    playwright: false, web_search: true, filesystem: false,
  });
  const [mysqlForm, setMysqlForm] = useState({ host: '', port: 3306, database: '', username: '', password: '' });
  const [testingMysql, setTestingMysql] = useState(false);

  /* ================================================================ */
  /*  Helpers                                                          */
  /* ================================================================ */
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  /* ================================================================ */
  /*  Fetch functions                                                  */
  /* ================================================================ */

  // ---- Copilot tool settings ----
  const fetchCopilotSettings = async () => {
    try {
      setLoadingCopilot(true);
      const response = await api.get('/chat/settings');
      setCopilotSettings(response.data);
      setToolToggles(response.data.tools_enabled || {});
      if (response.data.mysql_connection) {
        setMysqlForm((prev) => ({
          ...prev,
          host: response.data.mysql_connection.host || '',
          port: response.data.mysql_connection.port || 3306,
          database: response.data.mysql_connection.database || '',
          username: response.data.mysql_connection.username || '',
          password: '',
        }));
      }
    } catch (err) {
      console.error('Failed to load QAssure AI settings:', err);
    } finally {
      setLoadingCopilot(false);
    }
  };

  const saveCopilotSettings = async () => {
    try {
      setSavingCopilot(true);
      const payload = { tools_enabled: toolToggles };
      if (toolToggles.mysql && mysqlForm.host && mysqlForm.password) {
        payload.mysql_connection = {
          host: mysqlForm.host,
          port: parseInt(mysqlForm.port) || 3306,
          database: mysqlForm.database,
          username: mysqlForm.username,
          password: mysqlForm.password,
        };
      }
      await api.put('/chat/settings', payload);
      showSnackbar('QAssure AI settings saved successfully!', 'success');
      fetchCopilotSettings();
    } catch (err) {
      showSnackbar('Failed to save QAssure AI settings: ' + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setSavingCopilot(false);
    }
  };

  const testMysqlConnection = async () => {
    try {
      setTestingMysql(true);
      const response = await api.post('/chat/settings/test-mysql', {
        host: mysqlForm.host,
        port: parseInt(mysqlForm.port) || 3306,
        database: mysqlForm.database,
        username: mysqlForm.username,
        password: mysqlForm.password,
      });
      if (response.data.success) {
        showSnackbar(`MySQL connection successful! (${response.data.server_version})`, 'success');
      } else {
        showSnackbar(`MySQL connection failed: ${response.data.message}`, 'error');
      }
    } catch (err) {
      showSnackbar('MySQL test failed: ' + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setTestingMysql(false);
    }
  };

  const removeMysqlConnection = async () => {
    try {
      await api.delete('/chat/settings/mysql');
      showSnackbar('MySQL connection removed', 'success');
      setMysqlForm({ host: '', port: 3306, database: '', username: '', password: '' });
      setToolToggles((prev) => ({ ...prev, mysql: false }));
      fetchCopilotSettings();
    } catch (err) {
      showSnackbar('Failed to remove MySQL connection: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };

  // ---- App / Similarity settings ----
  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/settings');
      setThreshold(response.data.similarity_threshold);
      setSelectedModel(response.data.embedding_model);
      setSupportedModels(response.data.supported_models || {});
      setModelsStatus(response.data.models_status || {});
      setEmbeddingStats(response.data.embedding_stats || null);
      setCanEdit(response.data.can_edit || false);
      fetchSmartSearchSettings();
    } catch (err) {
      showSnackbar('Failed to load settings: ' + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchSmartSearchSettings = async () => {
    try {
      setLoadingSmartSearch(true);
      const response = await api.get('/search/settings');
      setSmartSearchSettings(response.data);
      if (response.data?.llm_settings) {
        const s = response.data.llm_settings;
        setMinSimilarity(Math.round((s.min_similarity_threshold || 0.5) * 100));
        setMinConfidence(Math.round((s.min_confidence_threshold || 0.5) * 100));
        setCacheTTL(s.cache_ttl_seconds || 60);
        setMaxResults(s.max_results || 50);
      }
    } catch (err) {
      console.error('Failed to load smart search settings:', err);
    } finally {
      setLoadingSmartSearch(false);
    }
  };

  const saveSmartSearchSettings = async () => {
    try {
      setSavingSmartSearch(true);
      await api.put('/search/settings', {
        min_similarity_threshold: minSimilarity / 100,
        min_confidence_threshold: minConfidence / 100,
        cache_ttl_seconds: cacheTTL,
        max_results: maxResults,
      });
      showSnackbar('Smart search settings saved successfully!', 'success');
      fetchSmartSearchSettings();
    } catch (err) {
      showSnackbar('Failed to save smart search settings: ' + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setSavingSmartSearch(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      const response = await api.put('/settings', {
        similarity_threshold: threshold,
        embedding_model: selectedModel,
      });
      showSnackbar('Settings saved successfully!', 'success');
      if (response.data.warnings) {
        setTimeout(() => showSnackbar(response.data.warnings.join(' '), 'warning'), 1500);
      }
      fetchSettings();
    } catch (err) {
      showSnackbar('Failed to save settings: ' + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setSaving(false);
    }
  };

  // ---- Embeddings ----
  const handlePopulateEmbeddings = async (regenerateAll = false) => {
    try {
      setPopulatingEmbeddings(true);
      const response = await api.post('/settings/populate-embeddings', { regenerate_all: regenerateAll });
      const data = response.data;
      if (data.status === 'started') {
        showSnackbar(`Started processing ${data.total} test cases.`, 'info');
        startStatusPolling();
      } else if (data.status === 'already_running') {
        showSnackbar(data.message, 'warning');
        startStatusPolling();
      } else if (data.status === 'completed') {
        showSnackbar(data.message, 'success');
        setPopulatingEmbeddings(false);
        fetchSettings();
      }
    } catch (err) {
      showSnackbar('Failed to populate embeddings: ' + (err.response?.data?.detail || err.message), 'error');
      setPopulatingEmbeddings(false);
    }
  };

  const startStatusPolling = () => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    pollIntervalRef.current = setInterval(async () => {
      try {
        const response = await api.get('/settings/embedding-population-status');
        const status = response.data;
        setEmbeddingProgress(status);
        if (!status.is_running) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          setPopulatingEmbeddings(false);
          showSnackbar(status.message || 'Embedding population completed', 'success');
          fetchSettings();
        }
      } catch (err) {
        console.error('Failed to poll embedding status:', err);
      }
    }, 2000);
  };

  // ---- Slack ----
  const fetchSlackStatus = async () => {
    try {
      setLoadingSlack(true);
      const [statusRes] = await Promise.all([
        api.get('/slack/status'),
        api.get('/slack/workspace'),
      ]);
      setSlackStatus(statusRes.data);
    } catch (err) {
      console.error('Failed to load Slack status:', err);
    } finally {
      setLoadingSlack(false);
    }
  };

  const handleSlackConnect = async () => {
    try {
      const response = await api.get('/slack/connect');
      const { auth_url } = response.data;
      const w = 600, h = 700;
      const left = window.screenX + (window.outerWidth - w) / 2;
      const top = window.screenY + (window.outerHeight - h) / 2;
      const popup = window.open(auth_url, 'slack-oauth', `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes`);
      const handleMessage = (event) => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.type === 'SLACK_OAUTH_CALLBACK') {
          window.removeEventListener('message', handleMessage);
          if (event.data.success) {
            showSnackbar(`Successfully connected to Slack as ${event.data.name || 'user'}!`, 'success');
            fetchSlackStatus();
          } else {
            showSnackbar(`Slack connection failed: ${event.data.error || 'Unknown error'}`, 'error');
          }
        }
      };
      window.addEventListener('message', handleMessage);
      const pollTimer = setInterval(() => {
        if (popup?.closed) { clearInterval(pollTimer); window.removeEventListener('message', handleMessage); fetchSlackStatus(); }
      }, 500);
    } catch (err) {
      showSnackbar('Failed to initiate Slack connection: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };

  const handleSlackDisconnect = async () => {
    try {
      await api.delete('/slack/disconnect');
      showSnackbar('Slack account disconnected', 'success');
      fetchSlackStatus();
    } catch (err) {
      showSnackbar('Failed to disconnect Slack: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };

  // ---- JIRA ----
  const fetchJiraStatus = async () => {
    try {
      setLoadingJira(true);
      const response = await api.get('/jira/status');
      setJiraStatus(response.data);
    } catch (err) {
      console.error('Failed to load JIRA status:', err);
    } finally {
      setLoadingJira(false);
    }
  };

  const handleJiraConnect = async () => {
    try {
      const response = await api.get('/jira/connect');
      const { auth_url } = response.data;
      const w = 600, h = 700;
      const left = window.screenX + (window.outerWidth - w) / 2;
      const top = window.screenY + (window.outerHeight - h) / 2;
      const popup = window.open(auth_url, 'jira-oauth', `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes`);
      const handleMessage = (event) => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.type === 'JIRA_OAUTH_CALLBACK') {
          window.removeEventListener('message', handleMessage);
          if (event.data.success) {
            showSnackbar(`Successfully connected to JIRA as ${event.data.name || 'user'}!`, 'success');
            fetchJiraStatus();
          } else {
            showSnackbar(`JIRA connection failed: ${event.data.error || 'Unknown error'}`, 'error');
          }
        }
      };
      window.addEventListener('message', handleMessage);
      const pollTimer = setInterval(() => {
        if (popup?.closed) { clearInterval(pollTimer); window.removeEventListener('message', handleMessage); fetchJiraStatus(); }
      }, 500);
    } catch (err) {
      showSnackbar('Failed to initiate JIRA connection: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };

  const handleJiraDisconnect = async () => {
    try {
      await api.delete('/jira/disconnect');
      showSnackbar('JIRA account disconnected', 'success');
      fetchJiraStatus();
    } catch (err) {
      showSnackbar('Failed to disconnect JIRA: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };

  /* ================================================================ */
  /*  Effects                                                          */
  /* ================================================================ */
  useEffect(() => {
    fetchSettings();
    fetchSlackStatus();
    fetchJiraStatus();
    fetchCopilotSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => { if (pollIntervalRef.current) clearInterval(pollIntervalRef.current); };
  }, []);

  useEffect(() => {
    const checkRunningTask = async () => {
      try {
        const response = await api.get('/settings/embedding-population-status');
        if (response.data.is_running) {
          setPopulatingEmbeddings(true);
          setEmbeddingProgress(response.data);
          startStatusPolling();
        }
      } catch (_) { /* ignore */ }
    };
    if (!loading) checkRunningTask();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  /* ================================================================ */
  /*  Loading state                                                    */
  /* ================================================================ */
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', flexDirection: 'column', gap: 2 }}>
        <CircularProgress size={48} />
        <Typography variant="body1" color="text.secondary">Loading settings…</Typography>
      </Box>
    );
  }

  const embeddingCoverage = embeddingStats
    ? Math.round((embeddingStats.with_embeddings / embeddingStats.total_test_cases) * 100) || 0
    : 0;

  /* ================================================================ */
  /*  Render                                                           */
  /* ================================================================ */
  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 960, mx: 'auto' }}>
      {/* Page header */}
      <Box sx={{ mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
          <SettingsIcon sx={{ fontSize: 28, color: 'primary.main' }} />
          <Typography variant="h5" fontWeight="bold">Settings</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Manage integrations, AI tools, and application configuration
        </Typography>
      </Box>

      {/* ==================== INTEGRATIONS ==================== */}
      <SectionHeader label="Integrations" />

      <JiraRow
        jiraStatus={jiraStatus}
        loadingJira={loadingJira}
        onRefresh={fetchJiraStatus}
        onConnect={handleJiraConnect}
        onDisconnect={handleJiraDisconnect}
      />

      <SlackRow
        slackStatus={slackStatus}
        loadingSlack={loadingSlack}
        onRefresh={fetchSlackStatus}
        onConnect={handleSlackConnect}
        onDisconnect={handleSlackDisconnect}
      />

      <GitHubRow onStatusChange={fetchCopilotSettings} />

      <GoogleDriveRow canEdit={canEdit} showSnackbar={showSnackbar} />

      {/* ==================== QASSURE AI ==================== */}
      <SectionHeader label="QAssure AI" />

      <AiToolsRow
        toolToggles={toolToggles}
        setToolToggles={setToolToggles}
        copilotSettings={copilotSettings}
        loadingCopilot={loadingCopilot}
        savingCopilot={savingCopilot}
        onSave={saveCopilotSettings}
        onRefresh={fetchCopilotSettings}
        mysqlForm={mysqlForm}
        setMysqlForm={setMysqlForm}
        testingMysql={testingMysql}
        onTestMysql={testMysqlConnection}
        onRemoveMysql={removeMysqlConnection}
      />

      {/* ==================== APPLICATION ==================== */}
      <SectionHeader label="Application" />

      {!canEdit && (
        <Alert severity="info" sx={{ mb: 1.5, borderRadius: 2 }}>
          <strong>View Only Mode</strong> — Only the super administrator can modify these settings.
        </Alert>
      )}

      <SimilarityRow
        threshold={threshold}
        setThreshold={setThreshold}
        selectedModel={selectedModel}
        setSelectedModel={setSelectedModel}
        supportedModels={supportedModels}
        modelsStatus={modelsStatus}
        canEdit={canEdit}
        saving={saving}
        onSave={handleSaveSettings}
      />

      <EmbeddingsRow
        embeddingStats={embeddingStats}
        embeddingCoverage={embeddingCoverage}
        selectedModel={selectedModel}
        canEdit={canEdit}
        populatingEmbeddings={populatingEmbeddings}
        embeddingProgress={embeddingProgress}
        onPopulate={handlePopulateEmbeddings}
        onRefresh={fetchSettings}
      />

      <SmartSearchRow
        smartSearchSettings={smartSearchSettings}
        loadingSmartSearch={loadingSmartSearch}
        savingSmartSearch={savingSmartSearch}
        minSimilarity={minSimilarity}
        setMinSimilarity={setMinSimilarity}
        minConfidence={minConfidence}
        setMinConfidence={setMinConfidence}
        cacheTTL={cacheTTL}
        setCacheTTL={setCacheTTL}
        maxResults={maxResults}
        setMaxResults={setMaxResults}
        onSave={saveSmartSearchSettings}
        onRefresh={fetchSmartSearchSettings}
      />

      {/* ==================== STORAGE ==================== */}
      <SectionHeader label="Storage" />

      <StorageRow />

      <HelpRow />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          elevation={6}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ApplicationSettings;
